import type { ModuleSearchResult } from '@/lib/api/search'
import type { SyntheticEvent } from 'react'
import { lazy, Suspense } from 'react'
import { CreateLinkedWorkDialog } from '@/components/time-entry/CreateLinkedWorkDialog'
import { useClientPortal } from '@/contexts/ClientPortalContext'
import { usePermissions } from '@/hooks/usePermissions'
import { useVisibleModuleTypes } from '@/hooks/useVisibleModuleTypes'
import { getClientPortalContextFromPathname } from '@/lib/api/client-portal-context'
import { cn } from '@/lib/utils'
import { getModuleLinkMeta } from '@/modules/module-link'
import { PlusIcon } from '@phosphor-icons/react'

import { ModuleTypeEnum, PermissionActionEnum } from '@scrambl/shared'

// The full create dialogs are large; pickers load one only when it opens.
const CreateProjectDialog = lazy(() =>
  import('@/pages/projects/_components/CreateProjectDialog').then(module => ({
    default: module.CreateProjectDialog,
  })),
)
const CreateEntityDialog = lazy(() =>
  import('@/pages/entities/_components/CreateEntityDialog').then(module => ({
    default: module.CreateEntityDialog,
  })),
)
const CreateContactDialog = lazy(() =>
  import('@/pages/contacts/_components/CreateContactDialog').then(module => ({
    default: module.CreateContactDialog,
  })),
)
const CreateDealDialog = lazy(() =>
  import('@/pages/deals/_components/create-deal-dialog').then(module => ({
    default: module.CreateDealDialog,
  })),
)

/**
 * Record types a module picker can create on the spot, in the order their
 * "New …" rows appear. Each opens that module's own create dialog.
 */
export const PICKER_CREATABLE_TYPES = [
  'PROJECT',
  'ENTITY',
  'CONTACT',
  'DEAL',
  'TASK',
  'TASK_GROUP',
] as const

export type PickerCreatableType = (typeof PICKER_CREATABLE_TYPES)[number]

const CREATE_PERMISSION_MODULE: Record<PickerCreatableType, ModuleTypeEnum> = {
  PROJECT: ModuleTypeEnum.PROJECT,
  ENTITY: ModuleTypeEnum.ENTITY,
  CONTACT: ModuleTypeEnum.CONTACT,
  DEAL: ModuleTypeEnum.DEAL,
  TASK: ModuleTypeEnum.TASK,
  TASK_GROUP: ModuleTypeEnum.TASK,
}

// Tasks and groups are created inside a project picked in the dialog.
const PROJECT_SCOPED_TYPES = new Set<PickerCreatableType>([
  'TASK',
  'TASK_GROUP',
])

/**
 * The picker types this person may create here: off in client portals, and
 * otherwise limited to modules enabled for the space (feature flags cover
 * personal accounts) that they hold create permission for.
 */
export function usePickerCreatableTypes(
  pickerTypes: readonly string[],
  enabled: boolean,
): PickerCreatableType[] {
  // Like useIsPortalView, but pickers also render outside a router (dialogs,
  // tests), so the URL check reads the window location directly.
  const portal = useClientPortal()
  const isPortalView =
    !!portal?.isPortalView ||
    !!getClientPortalContextFromPathname(window.location.pathname)
  const { checkPermission } = usePermissions()
  const visibleTypes = useVisibleModuleTypes([...pickerTypes])
  if (!enabled || isPortalView) return []
  return PICKER_CREATABLE_TYPES.filter(
    type =>
      visibleTypes.includes(type) &&
      checkPermission(
        CREATE_PERMISSION_MODULE[type],
        PermissionActionEnum.CREATE,
      ),
  )
}

/** "project", "client" (or the space's own word), "task group". */
export function getCreatableNoun(type: PickerCreatableType): string {
  return getModuleLinkMeta(type).label.toLowerCase()
}

interface CreateRecordRowProps {
  type: PickerCreatableType
  /** The search text the record is named after; blank shows "New project". */
  name: string
  isFocused?: boolean
  onCreate: (type: PickerCreatableType) => void
  onMouseEnter?: () => void
}

/** One "New project “Acme”" row under a picker's results. */
export function CreateRecordRow({
  type,
  name,
  isFocused = false,
  onCreate,
  onMouseEnter,
}: CreateRecordRowProps) {
  const meta = getModuleLinkMeta(type)
  const trimmedName = name.trim()

  return (
    <button
      type="button"
      data-edit-trigger="true"
      className={cn(
        'transition-tonal flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-left',
        isFocused ? 'bg-accent text-accent-foreground' : 'hover:bg-muted/50',
      )}
      data-focused={isFocused || undefined}
      ref={el => {
        if (isFocused && el) el.scrollIntoView({ block: 'nearest' })
      }}
      // Keeps focus in the search box so the picker doesn't close first.
      onMouseDown={event => {
        if (event.button !== 0) return
        event.preventDefault()
        event.stopPropagation()
        onCreate(type)
      }}
      onMouseEnter={onMouseEnter}
    >
      <span
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
          meta.bgClass,
        )}
      >
        <PlusIcon className={cn('h-3.5 w-3.5', meta.textClass)} />
      </span>
      <span className="min-w-0 flex-1 truncate text-sm">
        New {getCreatableNoun(type)}
        {trimmedName && (
          <>
            {' '}
            <span className="font-medium">&ldquo;{trimmedName}&rdquo;</span>
          </>
        )}
      </span>
      {PROJECT_SCOPED_TYPES.has(type) && (
        <span className="shrink-0 text-[11px] text-muted-foreground">
          In a project you choose
        </span>
      )}
    </button>
  )
}

interface CreateRecordFromPickerDialogProps {
  /** The type being created; null keeps every dialog closed. */
  type: PickerCreatableType | null
  initialName: string
  onClose: () => void
  /** The created record, shaped like a search result so the picker selects it. */
  onCreated: (result: ModuleSearchResult) => void
}

// Hosts are often forms or clickable rows themselves (a task dialog, a table
// cell), and React events bubble through portals: without this, submitting the
// create dialog also submits the host, Enter / Cmd+Enter reach its shortcuts,
// and clicks reach its row handlers.
const stopBubbling = (event: SyntheticEvent) => event.stopPropagation()

/**
 * Opens the module's own create dialog from a picker, prefilled with the
 * search text, and hands the new record back for the picker to select. Each
 * dialog invalidates its module's lists and caches after it saves.
 */
export function CreateRecordFromPickerDialog({
  type,
  initialName,
  onClose,
  onCreated,
}: CreateRecordFromPickerDialogProps) {
  if (!type) return null

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose()
  }

  const dialog = (() => {
    switch (type) {
      case 'PROJECT':
        return (
          <CreateProjectDialog
            open
            onOpenChange={handleOpenChange}
            initialName={initialName}
            onProjectCreated={project =>
              onCreated({
                id: project.id,
                name: project.name,
                identifier: project.code || undefined,
                type: 'PROJECT',
                relevanceScore: 1,
                entityId: project.entityId || undefined,
              })
            }
          />
        )
      case 'ENTITY':
        return (
          <CreateEntityDialog
            open
            onOpenChange={handleOpenChange}
            initialName={initialName}
            onEntityCreated={entity =>
              onCreated({
                id: entity.id,
                name: entity.name,
                type: 'ENTITY',
                relevanceScore: 1,
              })
            }
          />
        )
      case 'CONTACT':
        return (
          <CreateContactDialog
            open
            onOpenChange={handleOpenChange}
            initialName={initialName}
            onContactCreated={contact =>
              onCreated({
                id: contact.id,
                name:
                  [contact.firstName, contact.lastName]
                    .filter(Boolean)
                    .join(' ') ||
                  contact.email ||
                  initialName,
                type: 'CONTACT',
                relevanceScore: 1,
                entityId: contact.entityId || undefined,
              })
            }
          />
        )
      case 'DEAL':
        return (
          <CreateDealDialog
            open
            onOpenChange={handleOpenChange}
            defaultName={initialName}
            onDealCreated={deal =>
              onCreated({
                id: deal.id,
                name: deal.name,
                type: 'DEAL',
                relevanceScore: 1,
                entityId: deal.entityId || undefined,
              })
            }
          />
        )
      case 'TASK':
      case 'TASK_GROUP':
        return (
          <CreateLinkedWorkDialog
            open
            onOpenChange={handleOpenChange}
            type={type}
            initialName={initialName}
            onCreated={onCreated}
          />
        )
    }
  })()

  return (
    <div
      className="contents"
      onSubmit={stopBubbling}
      onKeyDown={stopBubbling}
      onClick={stopBubbling}
      onMouseDown={stopBubbling}
      onPointerDown={stopBubbling}
    >
      <Suspense fallback={null}>{dialog}</Suspense>
    </div>
  )
}
